import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPaymentTerms, updateShipperPaymentTerms } from '../../api/endpoints/payments';
import type { GstMode } from '../../api/types';
import styles from './Payments.module.css';
import { useOpsUser } from '../ownership/opsUser';
import { Button } from '../../components/ui/Button';

export function ShipperPaymentTerms({
  userId,
  gstin,
  paymentTermsDays,
  gstMode,
  gstRatePct,
}: {
  userId: number;
  gstin: string | null | undefined;
  paymentTermsDays?: number | null;
  gstMode?: GstMode | null;
  gstRatePct?: number | null;
}) {
  const { t } = useTranslation();
  const { isManager } = useOpsUser();
  const settings = useQuery({ queryKey: ['payments', 'settings'], queryFn: getPaymentTerms });
  const [days, setDays] = useState(paymentTermsDays == null ? '' : String(paymentTermsDays));
  const initialPair = gstMode && gstRatePct != null ? `${gstMode}:${gstRatePct}` : '';
  const [pair, setPair] = useState(initialPair);
  const client = useQueryClient();
  const initialDays = paymentTermsDays == null ? '' : String(paymentTermsDays);
  const [baseline, setBaseline] = useState({ days: initialDays, pair: initialPair });
  const seen = useRef(`${userId}:${initialDays}:${initialPair}`);
  const dirty = days !== baseline.days || pair !== baseline.pair;
  const [saved, setSaved] = useState(false);
  const [serverChanged, setServerChanged] = useState(false);
  const saving = useRef(false);
  useEffect(() => {
    const key = `${userId}:${initialDays}:${initialPair}`;
    if (seen.current === key) return;
    seen.current = key;
    if (dirty) {
      setServerChanged(true);
    } else {
      setServerChanged(false);
      setDays(initialDays);
      setPair(initialPair);
      setBaseline({ days: initialDays, pair: initialPair });
    }
  }, [userId, initialDays, initialPair, dirty]);
  const mutation = useMutation({
    retry: false,
    mutationFn: () => {
      const request: {
        paymentTermsDays?: number | null;
        gstMode?: GstMode | null;
        gstRatePct?: number | null;
      } = {};
      if (days !== baseline.days) request.paymentTermsDays = days === '' ? null : Number(days);
      if (pair !== baseline.pair) {
        const [mode, rate] = pair.split(':');
        request.gstMode = pair ? (mode as GstMode) : null;
        request.gstRatePct = pair ? Number(rate) : null;
      }
      return updateShipperPaymentTerms(userId, request);
    },
    onSuccess: async (result) => {
      const savedDays = result.paymentTermsDays == null ? '' : String(result.paymentTermsDays);
      const savedPair =
        result.gstMode && result.gstRatePct != null ? `${result.gstMode}:${result.gstRatePct}` : '';
      setDays(savedDays);
      setPair(savedPair);
      setBaseline({ days: savedDays, pair: savedPair });
      setSaved(true);
      setServerChanged(false);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['payments'] }),
        client.invalidateQueries({ queryKey: ['admin', 'users'] }),
      ]);
    },
  });
  const invalidDays =
    days !== '' && (!/^\d+$/.test(days) || Number(days) < 0 || Number(days) > 180);
  const options =
    settings.data?.invoiceGstOptions
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean) ?? [];
  return (
    <section className={`${styles.card} ${styles.paymentTerms}`}>
      <h3>{t('payments.shipperPaymentTerms.heading')}</h3>
      {!gstin && <p role="status">{t('payments.shipperPaymentTerms.noGstin')}</p>}
      <p>
        {t('payments.shipperPaymentTerms.effectiveTerms')}{' '}
        {days ||
          settings.data?.defaultShipperPaymentTermsDays ||
          (settings.data?.defaultShipperPaymentTermsDays === 0 ? 0 : 'unavailable')}{' '}
        {t('payments.shipperPaymentTerms.daysSeparator')}{' '}
        {pair
          ? pair.replace(':', ' ') + ' %'
          : settings.data
            ? `${settings.data.defaultInvoiceGstMode} ${settings.data.defaultInvoiceGstRate} %`
            : 'GST default unavailable'}
        {!days || !pair ? ' (blank overrides inherit platform defaults)' : ' (shipper overrides)'}
      </p>
      {!isManager && <p>{t('payments.shipperPaymentTerms.viewOnly')}</p>}
      <div className={styles.termsFields}>
        <label className={styles.field}>
          {t('payments.shipperPaymentTerms.daysLabel')}
          <input
            aria-label={t('payments.shipperPaymentTerms.daysLabel')}
            type="number"
            min="0"
            max="180"
            disabled={!isManager || mutation.isPending}
            value={days}
            placeholder={t('payments.shipperPaymentTerms.usePlatformDefault')}
            onChange={(event) => {
              setDays(event.target.value);
              setSaved(false);
            }}
          />
        </label>
        <label className={styles.field}>
          {t('payments.shipperPaymentTerms.gstOption')}
          <select
            aria-label={t('payments.shipperPaymentTerms.gstOption')}
            disabled={!isManager || mutation.isPending}
            value={pair}
            onChange={(event) => {
              setPair(event.target.value);
              setSaved(false);
            }}
          >
            <option value="">{t('payments.shipperPaymentTerms.usePlatformDefault')}</option>
            {options.map((option) => {
              const [mode, rate] = option.split(':');
              return (
                <option key={option} value={option}>
                  {mode} {rate} %
                </option>
              );
            })}
          </select>
        </label>
      </div>
      {invalidDays && <div role="alert">{t('payments.shipperPaymentTerms.invalidDays')}</div>}
      {mutation.isError && (
        <p role="alert">{t('payments.shipperPaymentTerms.saveError')}</p>
      )}
      {saved && <p role="status">{t('payments.shipperPaymentTerms.saved')}</p>}
      {serverChanged && (
        <p role="status">{t('payments.shipperPaymentTerms.serverChanged')}</p>
      )}
      {dirty && <p>{t('payments.shipperPaymentTerms.unsaved')}</p>}
      {isManager && (
        <div className={styles.termsActions}>
          <Button
            variant="primary"
            size="sm"
            disabled={
              invalidDays ||
              mutation.isPending ||
              !dirty ||
              serverChanged ||
              (Boolean(pair) && !options.includes(pair))
            }
            onClick={() => {
              if (saving.current) return;
              saving.current = true;
              mutation.mutate(undefined, {
                onSettled: () => {
                  saving.current = false;
                },
              });
            }}
          >
            {mutation.isPending ? 'Saving…' : 'Save shipper payment terms'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!dirty || mutation.isPending}
            onClick={() => {
              setDays(initialDays);
              setPair(initialPair);
              setBaseline({ days: initialDays, pair: initialPair });
              setServerChanged(false);
              setSaved(false);
              mutation.reset();
            }}
          >
            {t('payments.shipperPaymentTerms.discard')}
          </Button>
        </div>
      )}
    </section>
  );
}
